// PayOnlinePanel — the full-balance "Pay Online" step of Checkout Core.
//
// Replaces TenderTiles in the checkout drawer once Pay Online is chosen and the
// invoice is materialized. Mirrors the mobile app's Pay Online (jobviousMobile
// docs/superpowers/specs/2026-09-22-v1-tap-to-pay-completion.md §4a) — copy is
// kept identical on purpose so support can describe one flow.
//
//   QR code  — hosted_invoice_url, fetched fresh from GET /invoices/:id/fresh-url
//              on every open (stored links expire).
//   Text     — GET /invoices/:id/text-link → { maskedPhone }; POST sends it. The
//              client sends no number and no message; the server looks both up.
//              A 404 means the route isn't deployed yet → the button is hidden.
//   Email    — PUT /invoices/resend/:id.
//   Waiting  — polls GET /invoices/:id/balance every ~4s while mounted; calls
//              onPaid when isFullyPaid. Unmount stops the poll.
//
// Props:
//   invoiceId  string   — the materialized Stripe invoice id
//   amountDue  number   — dollars, for the header
//   onPaid()            — invoice settled
//   onDone()            — "Done — they'll pay later": close, leave the job open and due

import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { HTTPService } from '../../services/httpService';

const API = new HTTPService();
const TEAL = 'var(--color-brand-teal)';
const POLL_MS = 4000;

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const PayOnlinePanel = ({ invoiceId, amountDue, onPaid, onDone }) => {
  const [url, setUrl] = useState(null);
  const [urlState, setUrlState] = useState('loading'); // 'loading' | 'ready' | 'error'
  // undefined = still looking up / route missing (hidden); null = no mobile on file
  const [maskedPhone, setMaskedPhone] = useState(undefined);
  const [textAvailable, setTextAvailable] = useState(false);
  const [busy, setBusy] = useState(null); // 'text' | 'email' | null
  const [notice, setNotice] = useState(null); // { kind: 'success' | 'error', text }

  const loadUrl = useCallback(async () => {
    setUrlState('loading');
    try {
      const { data } = await API.get(`/invoices/${invoiceId}/fresh-url`);
      if (data && data.hosted_invoice_url) {
        setUrl(data.hosted_invoice_url);
        setUrlState('ready');
      } else {
        setUrl(null);
        setUrlState('error');
      }
    } catch (error) {
      console.error('fresh-url lookup failed', invoiceId, error);
      setUrl(null);
      setUrlState('error');
    }
  }, [invoiceId]);

  useEffect(() => { loadUrl(); }, [loadUrl]);

  // Text-link availability. 404 → server routes not deployed yet → hide.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/invoices/${invoiceId}/text-link`);
        if (cancelled) return;
        setMaskedPhone(data ? data.maskedPhone ?? null : null);
        setTextAvailable(true);
      } catch (error) {
        if (cancelled) return;
        setTextAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  // Waiting for payment — poll the authoritative balance while mounted.
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await API.get(`/invoices/${invoiceId}/balance`);
        if (!stopped && data && data.isFullyPaid) {
          stopped = true;
          clearInterval(timer);
          onPaid && onPaid();
        }
      } catch (error) {
        // Transient — keep polling; the next tick retries.
        console.error('balance poll failed', invoiceId, error);
      }
    };
    const timer = setInterval(tick, POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [invoiceId, onPaid]);

  const handleText = async () => {
    setBusy('text');
    setNotice(null);
    try {
      const { data } = await API.post(`/invoices/${invoiceId}/text-link`, {});
      if (data && data.success === false) {
        setNotice({ kind: 'error', text: data.message || "Couldn't text the link." });
      } else {
        setNotice({ kind: 'success', text: `Link texted to ···${maskedPhone}` });
      }
    } catch (error) {
      const msg = error?.response?.data?.message;
      setNotice({ kind: 'error', text: msg || "Couldn't text the link." });
    } finally {
      setBusy(null);
    }
  };

  const handleEmail = async () => {
    setBusy('email');
    setNotice(null);
    try {
      await API.put(`/invoices/resend/${invoiceId}`);
      setNotice({ kind: 'success', text: 'Invoice emailed' });
    } catch (error) {
      console.error('invoice resend failed', invoiceId, error);
      setNotice({ kind: 'error', text: "Couldn't email the invoice." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pay-online-panel">
      <div className="d-flex justify-content-between align-items-baseline mb-3">
        <span style={{ fontSize: 16, fontWeight: 600 }}>Pay online</span>
        <span style={{ fontSize: 22, fontWeight: 700 }}>{fmt(amountDue)}</span>
      </div>

      <div className="text-center mb-3">
        {urlState === 'loading' && (
          <div className="d-flex align-items-center justify-content-center" style={{ height: 180 }}>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          </div>
        )}
        {urlState === 'ready' && (
          <>
            <QRCodeCanvas value={url} size={180} level="M" includeMargin={true} />
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>Scan with your phone camera to pay</div>
          </>
        )}
        {urlState === 'error' && (
          <div className="py-4">
            <div className="mb-2" style={{ fontSize: 14 }}>Couldn't get the payment link</div>
            <button type="button" className="btn btn-link btn-sm" onClick={loadUrl}>
              <i className="fa-sharp fa-regular fa-rotate-right mr-1" /> Retry
            </button>
          </div>
        )}
      </div>

      {textAvailable && (
        <button
          type="button"
          className="btn btn-light btn-block d-flex align-items-center justify-content-center"
          style={{ fontSize: 14, fontWeight: 500 }}
          onClick={handleText}
          disabled={!maskedPhone || busy !== null}
        >
          {busy === 'text'
            ? <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
            : <i className="fa-sharp fa-regular fa-message-sms mr-2" />}
          {maskedPhone ? `Text the link · ···${maskedPhone}` : 'No mobile number on file'}
        </button>
      )}
      <button
        type="button"
        className="btn btn-light btn-block d-flex align-items-center justify-content-center mt-2"
        style={{ fontSize: 14, fontWeight: 500 }}
        onClick={handleEmail}
        disabled={busy !== null}
      >
        {busy === 'email'
          ? <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
          : <i className="fa-sharp fa-regular fa-envelope mr-2" />}
        Email the invoice
      </button>

      {notice && (
        <div
          className={`mt-2 text-center ${notice.kind === 'error' ? 'text-danger' : 'text-success'}`}
          style={{ fontSize: 13 }}
          role={notice.kind === 'error' ? 'alert' : 'status'}
        >
          {notice.text}
        </div>
      )}

      <div className="text-center text-muted my-3" style={{ fontSize: 13 }}>
        <span className="spinner-grow spinner-grow-sm mr-2" style={{ color: TEAL, width: 8, height: 8 }} aria-hidden="true" />
        Waiting for payment…
      </div>

      <div className="d-flex justify-content-between align-items-center border-top pt-3">
        <button type="button" className="btn btn-link btn-sm px-0" onClick={onDone}>
          Done — they'll pay later
        </button>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm px-0">
            Open the payment page ↗
          </a>
        )}
      </div>
    </div>
  );
};

export default PayOnlinePanel;
